
import { useState, useEffect } from 'react';
import { getEmergingCareers, getTopFutureCareers, Career } from '@/utils/careerData';
import { cn } from '@/lib/utils';

const FutureCareers = () => {
  const [emergingCareers, setEmergingCareers] = useState<Career[]>([]);
  const [topFutureCareers, setTopFutureCareers] = useState<Career[]>([]);
  const [selectedCareer, setSelectedCareer] = useState<Career | null>(null);
  const [activeTab, setActiveTab] = useState<'emerging' | 'top'>('emerging');
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      const emerging = getEmergingCareers();
      const top = getTopFutureCareers(6);
      
      setEmergingCareers(emerging);
      setTopFutureCareers(top);
      setSelectedCareer(emerging[0] || null);
      setIsLoading(false);
    }, 600);
  }, []);
  
  const displayedCareers = activeTab === 'emerging' ? emergingCareers : topFutureCareers;
  
  return (
    <div className="w-full max-w-4xl mx-auto px-4">
      <div className="flex justify-center space-x-4 mb-8">
        <button
          onClick={() => setActiveTab('emerging')}
          className={cn(
            "px-4 py-2 rounded-lg transition-all duration-200",
            activeTab === 'emerging'
              ? "bg-primary text-primary-foreground shadow-sm"
              : "bg-white/50 hover:bg-white/80 text-foreground"
          )}
        >
          Emerging Careers
        </button>
        <button
          onClick={() => setActiveTab('top')}
          className={cn(
            "px-4 py-2 rounded-lg transition-all duration-200",
            activeTab === 'top'
              ? "bg-primary text-primary-foreground shadow-sm"
              : "bg-white/50 hover:bg-white/80 text-foreground"
          )}
        >
          Top Future Careers
        </button>
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="glass-card rounded-xl p-6 animate-pulse">
              <div className="h-6 bg-muted rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-muted rounded w-full mb-2"></div>
              <div className="h-4 bg-muted rounded w-5/6 mb-2"></div>
              <div className="h-4 bg-muted rounded w-4/6"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {displayedCareers.map((career, index) => (
            <div 
              key={career.id}
              className={cn(
                "glass-card rounded-xl p-6 cursor-pointer transition-all duration-300",
                "hover:shadow-glass-hover transform hover:-translate-y-1",
                `animation-delay-${index * 100}`
              )}
              onClick={() => setSelectedCareer(career)}
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-lg leading-tight">{career.title}</h3>
                <div className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                  {career.futureRelevance}/10
                </div>
              </div>
              
              <p className="text-sm text-foreground/80 mb-4 line-clamp-3">
                {career.description}
              </p>
              
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>
                  {career.emergenceTimeframe === "current" 
                    ? "Available now" 
                    : career.emergenceTimeframe === "emerging"
                      ? "Emerging in 1-5 years"
                      : "Predicted in 5-10 years"}
                </span>
                <span>AI Risk: {career.aiRiskLevel}/10</span>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {selectedCareer && (
        <div className="mt-12 bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-soft animate-slide-up">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <h3 className="text-2xl font-bold">{selectedCareer.title}</h3>
                <div className="ml-auto flex items-center">
                  <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                    {selectedCareer.futureRelevance}/10
                  </div>
                </div>
              </div>
              
              <p className="mb-6">{selectedCareer.description}</p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-secondary mb-2">Key Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCareer.keySkills.map((skill, index) => (
                      <span 
                        key={index} 
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-secondary mb-2">Education Paths</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCareer.educationPaths.map((path, index) => (
                      <span 
                        key={index} 
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                      >
                        {path}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-secondary mb-2">Industry Trends</h4>
                  <ul className="space-y-2">
                    {selectedCareer.industryTrends.map((trend, index) => (
                      <li key={index} className="flex items-start">
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs mr-2">
                          {index + 1}
                        </span>
                        <span>{trend}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-secondary mb-2">Career Timeline</h4>
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-muted text-muted-foreground">
                          Development Stage
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-primary/10 text-primary">
                          {selectedCareer.emergenceTimeframe === "current" 
                            ? "Currently available" 
                            : selectedCareer.emergenceTimeframe === "emerging"
                              ? "Rapidly emerging"
                              : "Future projection"}
                        </span>
                      </div>
                    </div>
                    <div className="h-2 mb-4 overflow-hidden text-xs flex rounded bg-muted">
                      <div 
                        style={{ 
                          width: selectedCareer.emergenceTimeframe === "current" 
                            ? "90%" 
                            : selectedCareer.emergenceTimeframe === "emerging"
                              ? "50%"
                              : "30%"
                        }} 
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-secondary mb-2">AI Automation Risk</h4>
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block text-muted-foreground">
                          Low Risk
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-muted-foreground">
                          High Risk
                        </span>
                      </div>
                    </div>
                    <div className="h-2 mb-0 overflow-hidden text-xs flex rounded bg-muted">
                      <div 
                        style={{ 
                          width: `${selectedCareer.aiRiskLevel * 10}%`, 
                          backgroundColor: `hsl(${120 - selectedCareer.aiRiskLevel * 12}, 100%, 40%)`
                        }} 
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ease-out"
                      ></div>
                    </div>
                  </div>
                </div>
                
                {selectedCareer.traditionalEquivalent && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-secondary mb-2">Evolving From</h4>
                    <p className="text-muted-foreground">
                      This future role builds upon skills from the traditional career path of a <span className="font-medium text-foreground">{selectedCareer.traditionalEquivalent}</span>, but incorporates new technologies and approaches to meet emerging needs.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FutureCareers;
